<template>
  <div>
    <div class="row">
      <div class="form-group col-md-6">
        <label class="mb-1"><b><u class="section-title"><i class="fas fa-sliders-h"></i>&nbsp;&nbsp;Configuración General&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</u></b></label>
      </div>
      <div class="col-md-12"><hr></div>
    </div>

    <b-overlay :show="loading" rounded="sm">
      <form autocomplete="off" @submit.prevent="submitForm">

        <!-- ==================== INSTITUCIÓN ==================== -->
        <div class="panel-block mb-2">
          <div class="panel-title">Institución</div>

          <div class="row">
            <div class="form-group col-md-8">
              <label for="social_name"><b>Nombre o Razón Social&nbsp;<span class="asterisk">*</span></b></label>
              <input v-model="form.social_name" type="text" class="form-control" id="social_name" placeholder="Nombre de la institución">
              <small class="text-muted">Es el remitente que verá quien reciba los correos del sistema.</small>
              <span v-if="errors.social_name" class="text-danger validField d-block"><b>{{ errors.social_name }}</b></span>
            </div>

            <div class="form-group col-md-4">
              <label for="country"><b>País&nbsp;<span class="asterisk">*</span></b></label>
              <select v-model="form.country" class="form-control" id="country">
                <option :value="null">Seleccione...</option>
                <option v-for="c in countries" :key="c.value" :value="c.value">{{ c.label }}</option>
              </select>
              <small class="text-muted">Determina cómo se valida el número de documento: en Chile se exige RUT.</small>
              <span v-if="errors.country" class="text-danger validField d-block"><b>{{ errors.country }}</b></span>
            </div>
          </div>

          <div class="row">
            <div class="form-group col-md-4">
              <label for="logo"><b>Logo</b></label>
              <input type="file" class="form-control" id="logo" accept="image/*" @change="onLogoChange">
              <span v-if="errors.logo" class="text-danger validField d-block"><b>{{ errors.logo }}</b></span>
              <div v-if="logoPreview" class="mt-2">
                <img :src="logoPreview" alt="Logo" class="b-radius shadow" style="max-height: 70px; max-width: 100%; object-fit: contain;">
              </div>
            </div>
          </div>
        </div>

        <!-- ==================== CORREO DE SALIDA ==================== -->
        <div class="panel-block mb-2">
          <div class="panel-title">Correo de Salida</div>

          <div class="row">
            <div class="form-group col-md-12">
              <label for="mail_host"><b>Host&nbsp;<span class="asterisk">*</span></b></label>
              <input v-model="form.mail_host" type="text" class="form-control" id="mail_host" placeholder="mail.midominio.com">
              <span v-if="errors.mail_host" class="text-danger validField d-block"><b>{{ errors.mail_host }}</b></span>
            </div>

            <div class="form-group col-md-4">
              <label for="mail_driver"><b>Driver&nbsp;<span class="asterisk">*</span></b></label>
              <input v-model="form.mail_driver" type="text" class="form-control" id="mail_driver" placeholder="smtp">
              <span v-if="errors.mail_driver" class="text-danger validField d-block"><b>{{ errors.mail_driver }}</b></span>
            </div>

            <div class="form-group col-md-4">
              <label for="mail_port"><b>Puerto&nbsp;<span class="asterisk">*</span></b></label>
              <input v-model="form.mail_port" type="text" class="form-control" id="mail_port" placeholder="465">
              <span v-if="errors.mail_port" class="text-danger validField d-block"><b>{{ errors.mail_port }}</b></span>
            </div>

            <div class="form-group col-md-4">
              <label for="mail_encryption"><b>Encriptación</b></label>
              <select v-model="form.mail_encryption" class="form-control" id="mail_encryption">
                <option :value="null">Ninguna</option>
                <option value="ssl">ssl</option>
                <option value="tls">tls</option>
              </select>
              <span v-if="errors.mail_encryption" class="text-danger validField d-block"><b>{{ errors.mail_encryption }}</b></span>
            </div>

            <div class="form-group col-md-6">
              <label for="mail_username"><b>Email&nbsp;<span class="asterisk">*</span></b></label>
              <input v-model="form.mail_username" type="email" class="form-control" id="mail_username" placeholder="contacto@midominio.com">
              <small class="text-muted">Se usa como usuario del SMTP y como dirección remitente.</small>
              <span v-if="errors.mail_username" class="text-danger validField d-block"><b>{{ errors.mail_username }}</b></span>
            </div>

            <div class="form-group col-md-6">
              <label for="mail_password"><b>Contraseña<span v-if="!hasMailPassword">&nbsp;<span class="asterisk">*</span></span></b></label>
              <input v-model="form.mail_password" type="password" class="form-control" id="mail_password"
                :placeholder="hasMailPassword ? '••••••••' : 'Contraseña del correo'">
              <small v-if="hasMailPassword" class="text-muted">Ya hay una contraseña guardada. Déjalo en blanco para no cambiarla.</small>
              <span v-if="errors.mail_password" class="text-danger validField d-block"><b>{{ errors.mail_password }}</b></span>
            </div>
          </div>
        </div>


        <div class="panel-block mb-2">
          <div class="panel-title">Colores de la plataforma y los correos</div>
          <div class="row">
            <div class="col-md-12"><small class="text-muted"><b>Acento</b></small></div>
            <div class="form-group col-md-6">
              <label for="theme_accent"><b>Color</b></label>
              <div class="input-group">
                <input v-model="form.theme_accent" type="color" class="form-control color-picker" id="theme_accent">
                <input v-model="form.theme_accent" type="text" class="form-control" maxlength="7" placeholder="#000000">
              </div>
              <small class="text-muted">Subrayado de títulos, barrita del ítem activo y franja de los correos</small>
              <span v-if="errors.theme_accent" class="text-danger validField d-block"><b>{{ errors.theme_accent }}</b></span>
            </div>
            <div class="col-md-12 mt-1"><small class="text-muted"><b>Barra de menú y cabecera</b></small></div>
            <div class="form-group col-md-6">
              <label for="theme_bar"><b>Fondo</b></label>
              <div class="input-group">
                <input v-model="form.theme_bar" type="color" class="form-control color-picker" id="theme_bar">
                <input v-model="form.theme_bar" type="text" class="form-control" maxlength="7" placeholder="#000000">
              </div>
              <small class="text-muted">Menú lateral, barra superior y cabecera de los correos</small>
              <span v-if="errors.theme_bar" class="text-danger validField d-block"><b>{{ errors.theme_bar }}</b></span>
            </div>
            <div class="form-group col-md-6">
              <label for="theme_bar_text"><b>Letra</b></label>
              <div class="input-group">
                <input v-model="form.theme_bar_text" type="color" class="form-control color-picker" id="theme_bar_text">
                <input v-model="form.theme_bar_text" type="text" class="form-control" maxlength="7" placeholder="#000000">
              </div>
              <small class="text-muted">Texto del menú y de la barra superior</small>
              <span v-if="errors.theme_bar_text" class="text-danger validField d-block"><b>{{ errors.theme_bar_text }}</b></span>
            </div>
            <div class="col-md-12 mt-1"><small class="text-muted"><b>Ítem activo del menú</b></small></div>
            <div class="form-group col-md-6">
              <label for="theme_menu_active"><b>Fondo</b></label>
              <div class="input-group">
                <input v-model="form.theme_menu_active" type="color" class="form-control color-picker" id="theme_menu_active">
                <input v-model="form.theme_menu_active" type="text" class="form-control" maxlength="7" placeholder="#000000">
              </div>
              <small class="text-muted">Fondo del elemento seleccionado</small>
              <span v-if="errors.theme_menu_active" class="text-danger validField d-block"><b>{{ errors.theme_menu_active }}</b></span>
            </div>
            <div class="form-group col-md-6">
              <label for="theme_menu_active_text"><b>Letra</b></label>
              <div class="input-group">
                <input v-model="form.theme_menu_active_text" type="color" class="form-control color-picker" id="theme_menu_active_text">
                <input v-model="form.theme_menu_active_text" type="text" class="form-control" maxlength="7" placeholder="#000000">
              </div>
              <small class="text-muted">Texto del elemento seleccionado</small>
              <span v-if="errors.theme_menu_active_text" class="text-danger validField d-block"><b>{{ errors.theme_menu_active_text }}</b></span>
            </div>
            <div class="col-md-12 mt-1"><small class="text-muted"><b>Submenú</b></small></div>
            <div class="form-group col-md-6">
              <label for="theme_menu_sub"><b>Fondo</b></label>
              <div class="input-group">
                <input v-model="form.theme_menu_sub" type="color" class="form-control color-picker" id="theme_menu_sub">
                <input v-model="form.theme_menu_sub" type="text" class="form-control" maxlength="7" placeholder="#000000">
              </div>
              <small class="text-muted">Fondo del submenú desplegado</small>
              <span v-if="errors.theme_menu_sub" class="text-danger validField d-block"><b>{{ errors.theme_menu_sub }}</b></span>
            </div>
            <div class="form-group col-md-6">
              <label for="theme_menu_sub_text"><b>Letra</b></label>
              <div class="input-group">
                <input v-model="form.theme_menu_sub_text" type="color" class="form-control color-picker" id="theme_menu_sub_text">
                <input v-model="form.theme_menu_sub_text" type="text" class="form-control" maxlength="7" placeholder="#000000">
              </div>
              <small class="text-muted">Texto del submenú</small>
              <span v-if="errors.theme_menu_sub_text" class="text-danger validField d-block"><b>{{ errors.theme_menu_sub_text }}</b></span>
            </div>
            <div class="col-md-12 mt-1"><small class="text-muted"><b>Botón de acción</b></small></div>
            <div class="form-group col-md-6">
              <label for="theme_action"><b>Fondo</b></label>
              <div class="input-group">
                <input v-model="form.theme_action" type="color" class="form-control color-picker" id="theme_action">
                <input v-model="form.theme_action" type="text" class="form-control" maxlength="7" placeholder="#000000">
              </div>
              <small class="text-muted">Botón Guardar y botón de los correos</small>
              <span v-if="errors.theme_action" class="text-danger validField d-block"><b>{{ errors.theme_action }}</b></span>
            </div>
            <div class="form-group col-md-6">
              <label for="theme_action_text"><b>Letra</b></label>
              <div class="input-group">
                <input v-model="form.theme_action_text" type="color" class="form-control color-picker" id="theme_action_text">
                <input v-model="form.theme_action_text" type="text" class="form-control" maxlength="7" placeholder="#000000">
              </div>
              <small class="text-muted">Texto del botón</small>
              <span v-if="errors.theme_action_text" class="text-danger validField d-block"><b>{{ errors.theme_action_text }}</b></span>
            </div>
            <div class="col-md-12 mt-1">
              <small class="text-muted d-block mb-1">
                El fondo de las pantallas y los colores de la tabla se mantienen fijos a propósito.
              </small>
              <button type="button" class="btn btn-sm btn-secondary" @click="resetTheme">
                <i class="fas fa-undo fa-fw"></i> Restaurar colores por defecto
              </button>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-12 text-right">
            <button type="submit" class="btn btn-success" :disabled="saving">
              <i class="fas fa-save"></i> Guardar
            </button>
          </div>
        </div>

      </form>
    </b-overlay>
  </div>
</template>

<script>
import 'bootstrap-vue/dist/bootstrap-vue.css';
import { BOverlay } from 'bootstrap-vue';

export default {
  components: { BOverlay },
  data() {
    return {
      loading: false,
      saving: false,
      // Todas las claves declaradas desde el inicio: Vue 2 no hace reactivas
      // las propiedades añadidas después.
      countries: [],
      form: {
        social_name: '',
        country: 'CL',
        theme_accent: '#05B6B9',
        theme_bar: '#404E67',
        theme_menu_active: '#38445A',
        theme_menu_sub: '#4E5E77',
        theme_action: '#16D39A',
        theme_bar_text: '#DCDCDC',
        theme_menu_active_text: '#05B6B9',
        theme_menu_sub_text: '#DCDCDC',
        theme_action_text: '#FFFFFF',
        mail_driver: 'smtp',
        mail_host: '',
        mail_port: '',
        mail_encryption: null,
        mail_username: '',
        mail_password: '',
      },
      errors: {},
      logoFile: null,
      logoPreview: null,
      hasMailPassword: false,
    }
  },
  created() {
    this.getCountries();
    this.getRecord();
  },
  methods: {
    /**
     * Repone la paleta de fábrica. Son los mismos valores de config/theme.php; al
     * guardar, el backend los reconoce y los deja como estaban.
     */
    resetTheme() {
      this.form.theme_accent = '#05B6B9';
      this.form.theme_bar = '#404E67';
      this.form.theme_menu_active = '#38445A';
      this.form.theme_menu_sub = '#4E5E77';
      this.form.theme_action = '#16D39A';
      this.form.theme_bar_text = '#DCDCDC';
      this.form.theme_menu_active_text = '#05B6B9';
      this.form.theme_menu_sub_text = '#DCDCDC';
      this.form.theme_action_text = '#FFFFFF';
      toastr.info('Colores por defecto repuestos. Recuerda guardar.', 'Aviso', { icon: 'fa fa-info-circle' });
    },
    getCountries() {
      axios.get('/configuration/general/getCountries')
        .then(response => { this.countries = response.data.data })
        .catch(error => {
          console.error(error);
          toastr.error('No se pudo cargar la lista de países.', 'Error', { icon: 'fa fa-times-circle' });
        });
    },
    getRecord() {
      this.loading = true;
      axios.get('/configuration/general/record')
        .then(response => {
          const data = response.data.data;
          this.form.social_name = data.social_name || '';
          this.form.country = data.country || 'CL';
          if (data.theme) {
            this.form.theme_accent = data.theme.accent;
            this.form.theme_bar = data.theme.bar;
            this.form.theme_menu_active = data.theme.menu_active;
            this.form.theme_menu_sub = data.theme.menu_sub;
            this.form.theme_action = data.theme.action;
            this.form.theme_bar_text = data.theme.bar_text;
            this.form.theme_menu_active_text = data.theme.menu_active_text;
            this.form.theme_menu_sub_text = data.theme.menu_sub_text;
            this.form.theme_action_text = data.theme.action_text;
          }
          this.form.mail_driver = data.mail_driver || 'smtp';
          this.form.mail_host = data.mail_host || '';
          this.form.mail_port = data.mail_port || '';
          this.form.mail_encryption = data.mail_encryption || null;
          this.form.mail_username = data.mail_username || '';
          this.form.mail_password = '';
          this.hasMailPassword = data.has_mail_password;
          this.logoPreview = data.logo;
        })
        .catch(error => {
          console.error(error);
          toastr.error('No se pudo cargar la configuración.', 'Error', { icon: 'fa fa-times-circle' });
        })
        .finally(() => { this.loading = false });
    },
    onLogoChange(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.logoFile = file;
      this.logoPreview = URL.createObjectURL(file);
    },
    submitForm() {
      if (this.saving) return;
      this.saving = true;
      this.errors = {};

      const formData = new FormData();
      formData.append('social_name', this.form.social_name || '');
      formData.append('country', this.form.country || '');
      ['theme_accent','theme_bar','theme_menu_active','theme_menu_sub','theme_action',
       'theme_bar_text','theme_menu_active_text','theme_menu_sub_text','theme_action_text'].forEach(k => {
        formData.append(k, this.form[k] || '');
      });
      formData.append('mail_driver', this.form.mail_driver || '');
      formData.append('mail_host', this.form.mail_host || '');
      formData.append('mail_port', this.form.mail_port || '');
      if (this.form.mail_encryption) formData.append('mail_encryption', this.form.mail_encryption);
      formData.append('mail_username', this.form.mail_username || '');
      if (this.form.mail_password) formData.append('mail_password', this.form.mail_password);
      if (this.logoFile) formData.append('logo', this.logoFile);

      axios.post('/configuration/general', formData)
        .then(response => {
          if (response.data.status == 'error') {
            toastr.error(response.data.message, 'Error', { icon: 'fa fa-times-circle' });
          } else {
            toastr.success(response.data.message, 'Realizado', { icon: 'fa fa-check-circle' });
            this.logoFile = null;
            this.getRecord();
          }
        })
        .catch(error => {
          console.error(error);
          // Un 422 de FormRequest es mudo con el .catch heredado: hay que
          // desglosar los errores para que el usuario sepa qué campo falla.
          if (error.response && error.response.status === 422) {
            const bag = error.response.data.errors || {};
            Object.keys(bag).forEach(field => { this.$set(this.errors, field, bag[field][0]) });
            toastr.error('Revisa los campos marcados.', 'Datos no válidos', { icon: 'fa fa-times-circle' });
          } else {
            toastr.error('No se pudo guardar la configuración.', 'Error', { icon: 'fa fa-times-circle' });
          }
        })
        .finally(() => { this.saving = false });
    },
  }
}
</script>
